import { Dialog, Text, neutral } from "@flat-ui";
import { Box, Stack } from "@mui/material";
import { useState } from "react";
import { SlideProgressIndicator } from "@components/splash-screen/slide-progress-indicator";
import { AnalyticsFeedbackIntroBody } from "@components/analytics/in-house/feedback/analytics-feedback-intro-body";
import {
  FeedbackStepDefinition,
  SurveyAnswers,
} from "@components/analytics/in-house/feedback/analytics-feedback-types";
import { analyticsFeedbackStep1 } from "@components/analytics/in-house/feedback/analytics-feedback-step1";
import { analyticsFeedbackStep2 } from "@components/analytics/in-house/feedback/analytics-feedback-step2";
import { analyticsFeedbackStep3 } from "@components/analytics/in-house/feedback/analytics-feedback-step3";
import { analyticsFeedbackStep4 } from "@components/analytics/in-house/feedback/analytics-feedback-step4";
import { useTrackEvent } from "@utils/track-event/use-track-event";
import { AnalyticsFeedbackEvents } from "@utils/track-event/track-event-list";
import {
  dismissAnalyticsFeedbackPermanently,
  markAnalyticsFeedbackProvided,
  recordAnalyticsFeedbackDismissal,
} from "@components/analytics/in-house/feedback/analytics-feedback-storage";
import { useStellarApiClient } from "@hooks/use-stellar-api-client";
import { runtimeConfig } from "@src/runtime-config";
import { useAppSelector } from "@store/store-helper";
import { currentUserSelector } from "@store/user/user-selector";
import { EmailUtils } from "@stellar/utils";
import { selectedSdbCompanySelector } from "@store/sdb-company/sdb-company-selector";
import { sentryCaptureError } from "@utils/sentry-utils";
import { buildFeedbackMessage } from "@components/analytics/in-house/feedback/analytics-feedback-helper";

interface Props {
  /** Called when the feedback dialog should be closed. */
  onClose: () => void;
}

/** Ordered survey steps that make up the feedback flow. */
export const FEEDBACK_STEPS: FeedbackStepDefinition[] = [
  analyticsFeedbackStep1,
  analyticsFeedbackStep2,
  analyticsFeedbackStep3,
  analyticsFeedbackStep4,
];

const DIALOG_TITLE = "Premium Analytics Feedback";

/** Multi-step in-house analytics feedback survey shown in a dialog. */
export function AnalyticsFeedbackDialog({ onClose }: Props): JSX.Element {
  const [phase, setPhase] = useState<"intro" | "survey" | "done">("intro");
  const [currentStep, setCurrentStep] = useState(0);
  const [answers, setAnswers] = useState<SurveyAnswers>({
    decisions: [],
    decisionOther: "",
    oneThing: "",
    jobFunction: "",
    jobFunctionOther: "",
    hasOptedInToContact: false,
  });
  const { trackEvent } = useTrackEvent();
  const { stellarApiClient } = useStellarApiClient();
  const selectedCompany = useAppSelector(selectedSdbCompanySelector);
  const currentUser = useAppSelector(currentUserSelector);
  const isInternalUser = EmailUtils.isInternalUser(currentUser?.email ?? "");

  function trackViewStep(stepIndex: number): void {
    trackEvent({
      name: AnalyticsFeedbackEvents.viewStep,
      props: {
        stepIndex,
        stepName: FEEDBACK_STEPS[stepIndex].stepName,
      },
    });
  }

  async function goToNextStep(): Promise<void> {
    if (currentStep < FEEDBACK_STEPS.length - 1) {
      setCurrentStep((step) => step + 1);
      trackViewStep(currentStep + 1);
    } else {
      trackEvent({
        name: AnalyticsFeedbackEvents.completeSurvey,
        props: {
          hasOptedInToContact: answers.hasOptedInToContact,
          useCaseSelections: answers.decisions,
          useCaseSelectionCount: answers.decisions.length,
          hasAnsweredOneThing: answers.oneThing.trim().length > 0,
          jobFunction:
            answers.jobFunction === "other"
              ? answers.jobFunctionOther
              : answers.jobFunction,
        },
      });

      try {
        await stellarApiClient.feedback.submitFeedback({
          application: "SXG Dashboard",
          appVersion: runtimeConfig.appVersion,
          feedbackMessage: buildFeedbackMessage(answers),
          featureName: "Premium Analytics",

          companyId: selectedCompany?.id,
          userId: currentUser?.id,
          workspaceRole: selectedCompany?.role.company,
          hasUserAllowedContact: answers.hasOptedInToContact,
          isInternalUser,
        });
      } catch (error) {
        // Failing to call the feedback route should not block the user from using the app
        // Use Sentry to log the error
        sentryCaptureError({
          error,
          title: "Error calling feedback route for analytics premium",
        });
      }

      setPhase("done");
      markAnalyticsFeedbackProvided();
    }
  }

  function goToPreviousStep(): void {
    if (currentStep === 0) {
      setPhase("intro");
    } else {
      setCurrentStep((step) => step - 1);
      trackViewStep(currentStep - 1);
    }
  }

  /** Closes the dialog, but ignores clicks on the backdrop (outside the dialog). */
  function closeDialog(
    _: object,
    reason?: "backdropClick" | "escapeKeyDown"
  ): void {
    if (reason === "backdropClick") {
      return;
    }
    handleClose();
  }

  /**
   * Closes the dialog, recording the dismissal so the survey is shown again
   * after a cooldown, unless the user already completed it or completely opted out
   */
  function handleClose(): void {
    if (phase !== "done") {
      recordAnalyticsFeedbackDismissal();
    }
    onClose();
  }

  /** Permanently opts the user out of the survey and closes the dialog. */
  function handleDismissForever(): void {
    trackEvent({ name: AnalyticsFeedbackEvents.dismissForever });
    dismissAnalyticsFeedbackPermanently();
    onClose();
  }

  if (phase === "intro") {
    return (
      <Dialog
        open
        title={DIALOG_TITLE}
        onClose={closeDialog}
        confirmText="Yes, I'm in"
        onConfirm={() => {
          setCurrentStep(0);
          setPhase("survey");
          trackViewStep(0);
        }}
        secondaryText="Not right now"
        onSecondary={handleClose}
        cancelText="Don't show this again"
        onCancel={handleDismissForever}
      >
        <AnalyticsFeedbackIntroBody />
      </Dialog>
    );
  }

  if (phase === "done") {
    return (
      <Dialog open title={DIALOG_TITLE} onClose={closeDialog}>
        <Stack alignItems="center" gap={1} sx={{ py: 1 }}>
          <Text variant="heading20">Thank you</Text>
          <Text variant="bodyM" color={neutral[600]}>
            This directly shapes what we build next.
          </Text>
        </Stack>
      </Dialog>
    );
  }

  const { StepComponent } = FEEDBACK_STEPS[currentStep];

  return (
    <Dialog
      open
      title={DIALOG_TITLE}
      onClose={closeDialog}
      confirmText={
        currentStep === FEEDBACK_STEPS.length - 1 ? "Submit" : "Next"
      }
      isConfirmDisabled={!FEEDBACK_STEPS[currentStep].isValid(answers)}
      onConfirm={goToNextStep}
      cancelText="Back"
      onCancel={goToPreviousStep}
    >
      <>
        <StepComponent
          answers={answers}
          onAnswersChange={(patch) =>
            setAnswers((previous) => ({ ...previous, ...patch }))
          }
        />
        <Box sx={{ display: "flex", justifyContent: "center", mt: 3 }}>
          <SlideProgressIndicator
            activeScreenNumber={currentStep}
            totalNumberOfScreens={FEEDBACK_STEPS.length}
          />
        </Box>
      </>
    </Dialog>
  );
}
